/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Directive, Input, OnDestroy, WritableSignal, effect, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NgForm} from '@angular/forms';
import {finalize, Observable} from 'rxjs';
import * as _ from 'lodash';

import {UpdateSession} from '../../../components/session/update-session';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {UserProfileSaverService} from '../../../components/session/user-profile-saver.service';
import {AdnFormStateService} from '../../../components/util/adn-form-state.service';
import {Uuid} from '../../../components/util/uuid';
import {AppEventBusService} from '../events/app-event-bus.service';
import {
  clearFormValidation,
  createFormSubmitState,
  FormControlError,
  handleApiValidationErrors,
  updateFormWarnings
} from './form-submit-state';
import type {SignalAdminResourceApi, SignalAdminResourceModel} from './signal-admin-resource-api';
import {SignalAdminResourcePageState} from './signal-admin-resource-page-state';
import {syncNoteLinks} from '../directives/notes/notes-save-target';
import {deferSignalStateWrite} from '../util/template-state';

export function markSignalAdminResourceFormPristine(editForm: NgForm | null): void {
  if (!editForm) {
    return;
  }
  editForm.control.markAsPristine();
  editForm.control.markAsUntouched();
}

export function suspendSignalAdminResourceFormDirtyGuard(
  editForm: NgForm | null,
  formState: AdnFormStateService
): void {
  if (!editForm) {
    return;
  }
  markSignalAdminResourceFormPristine(editForm);
  formState.deregister();
}

/** Shared Angular form/save lifecycle for signal-native admin resource pages. */
@Directive()
export abstract class SignalAdminResourceEditPageBase<T extends SignalAdminResourceModel> implements OnDestroy {
  private editingModelState?: WritableSignal<T>;
  registeredForm: NgForm | null = null;

  get editingModel(): T {
    if (!this.editingModelState) {
      this.editingModelState = signal<T>({id: ''} as T);
    }
    return this.editingModelState();
  }

  set editingModel(value: T) {
    if (!this.editingModelState) {
      this.editingModelState = signal(value);
      return;
    }
    this.editingModelState.set(value);
  }

  protected readonly eventBus = inject(AppEventBusService);
  private readonly formState = inject(AdnFormStateService);
  private readonly localNetworkProfile = inject(LocalNetworkProfile);
  private readonly readOnlyUserProfile = inject(LocalUserProfile);
  private readonly userProfileSaver = inject(UserProfileSaverService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly submitState = createFormSubmitState();
  protected readonly formControlErrors: FormControlError[] = [];
  private readonly pendingState = signal(false);
  private readonly submitVersion = signal(0);
  private readonly pageState = signal<SignalAdminResourcePageState<T> | null>(null);
  private lastStateModel: T | null = null;
  private lastEditingRevision = 0;
  private warningsTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly formPristineFlag = signal(true);
  private readonly validSubmissionFlag = signal(false);
  private readonly pendingFlag = signal(false);
  private readonly isNewFlag = signal(false);
  private readonly saveDisabledFlag = signal(true);

  @Input()
  set state(value: SignalAdminResourcePageState<T>) {
    if (this.readOnlyUserProfile.isReadOnly()) {
      value.forceReadOnly();
    }
    this.pageState.set(value);
  }

  get state(): SignalAdminResourcePageState<T> {
    return this.pageState();
  }

  get model(): T {
    return this.state?.model();
  }

  protected constructor(
    private readonly api: SignalAdminResourceApi<T>,
    private readonly resourceName: string
  ) {
    effect(() => {
      const pageState = this.pageState();
      if (!pageState) {
        return;
      }
      const model = pageState.model();
      const editingRevision = pageState.editingRevision();
      if (model === this.lastStateModel) {
        return;
      }
      const previous = this.lastStateModel;
      this.lastStateModel = model;
      if (!previous || editingRevision !== this.lastEditingRevision) {
        const isEditingReplacement = !!previous && editingRevision !== this.lastEditingRevision;
        this.lastEditingRevision = editingRevision;
        this.resetEditingModel(model);
        if (isEditingReplacement && pageState.editingReplacementDirty() && this.registeredForm) {
          this.registeredForm.control.markAsDirty();
        }
      } else if (previous.id !== model.id) {
        this.editingModel = {...this.editingModel, id: model.id};
        this.bumpSubmitState();
      } else {
        this.editingModel.objectState = model.objectState;
        this.editingModel.validationWarnings = _.cloneDeep(model.validationWarnings || []);
        // Notes are linked by partial save from the analysis panel (notesSaveTarget).
        syncNoteLinks(model, this.editingModel);
        this.submitState.hasValidationWarnings = this.hasBlockingWarnings(model);
        this.scheduleWarningsUpdate();
        this.bumpSubmitState();
      }
    });
  }

  ngOnDestroy(): void {
    if (this.warningsTimer) {
      clearTimeout(this.warningsTimer);
    }
    this.formState.deregister();
  }

  get pending(): boolean {
    return this.pendingState();
  }

  get readOnly(): boolean {
    return this.state.readOnly();
  }

  get isNew(): boolean {
    return this.state.isNew();
  }

  get validSubmission(): boolean {
    this.submitVersion();
    return this.submitState.validSubmission;
  }

  get displayFormPristine(): boolean {
    return this.bufferDisplayFlag(this.formPristineFlag, !this.registeredForm || this.registeredForm.pristine);
  }

  get displayValidSubmission(): boolean {
    return this.bufferDisplayFlag(this.validSubmissionFlag, this.validSubmission);
  }

  get displayPending(): boolean {
    return this.bufferDisplayFlag(this.pendingFlag, this.pending);
  }

  get displayIsNew(): boolean {
    return this.bufferDisplayFlag(this.isNewFlag, this.isNew);
  }

  get hasValidationError(): boolean {
    this.submitVersion();
    return this.submitState.validationError;
  }

  get hasValidationWarnings(): boolean {
    this.submitVersion();
    return this.submitState.hasValidationWarnings;
  }

  get apiErrors(): Array<{key: string; apiText: string}> {
    this.submitVersion();
    return this.submitState.apiErrors;
  }

  get clientErrors(): any[] {
    this.submitVersion();
    return this.submitState.clientErrors;
  }

  get bulkError(): boolean | undefined {
    this.submitVersion();
    return this.submitState.bulkError;
  }

  registerForm(editForm: NgForm): void {
    if (this.registeredForm === editForm) {
      return;
    }
    this.registeredForm = editForm;
    this.submitState.ngForm = editForm;
    this.registerFormState(editForm);
    this.scheduleWarningsUpdate();
  }

  submit(
    editForm: NgForm,
    options?: {skipPristine?: boolean; additionalInvalid?: boolean; allowInvalidAfterServerValidation?: boolean}
  ): void {
    const invalid = editForm.invalid && !(options?.allowInvalidAfterServerValidation && this.hasValidationError);
    const blocked = options?.skipPristine
      ? this.pending || this.readOnly || !!options.additionalInvalid || editForm.pending || invalid
      : this.isSaveDisabled(editForm, !!options?.additionalInvalid) || invalid;
    if (blocked) {
      return;
    }

    this.pendingState.set(true);
    this.submitState.validSubmission = false;
    clearFormValidation(this.submitState, this.formControlErrors);
    this.bumpSubmitState();

    if (!this.editingModel.id) {
      this.editingModel.id = Uuid.generate();
    }
    // The notes panel updates page state independently. Synchronize immediately
    // so a full save cannot race its partial-save request and wipe note links.
    syncNoteLinks(this.state.model(), this.editingModel);
    this.prepareForSubmit(this.editingModel);
    if (this.submitState.clientErrors.length) {
      this.pendingState.set(false);
      this.applySaveError({
        status: 400,
        data: {type: 'ValidationResult', errors: this.submitState.clientErrors}
      }, editForm);
      this.eventBus.emit('SUBMIT_PROCESSED');
      return;
    }
    const payload = _.cloneDeep(this.editingModel);
    this.prepareSubmitPayload(payload);

    this.persist(payload).pipe(
      finalize(() => {
        this.pendingState.set(false);
        this.eventBus.emit('SUBMIT_PROCESSED');
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (saved) => this.applySave(saved, editForm),
      error: (response) => this.applySaveError(response, editForm)
    });
  }

  cancel(editForm: NgForm, additionallyInvalid = false): void {
    if (this.isSaveDisabled(editForm, additionallyInvalid)) {
      return;
    }
    this.submitState.validSubmission = false;
    this.resetEditingModel(this.state.model());
    markSignalAdminResourceFormPristine(editForm);
    this.bumpSubmitState();
  }

  isSaveDisabled(editForm: NgForm, additionallyInvalid = false): boolean {
    return this.readOnly || additionallyInvalid || editForm.pristine || this.pending || editForm.pending || editForm.invalid;
  }

  /** Buffered template binding for save controls whose state can settle during change detection. */
  displaySaveDisabled(editForm: NgForm, additionallyInvalid = false): boolean {
    return this.bufferDisplayFlag(this.saveDisabledFlag, this.isSaveDisabled(editForm, additionallyInvalid));
  }

  dismissSaveMessage(): void {
    this.submitState.validSubmission = false;
    this.bumpSubmitState();
  }

  markExternalFormDirty(): void {
    this.formState.markDirty();
  }

  protected isFormDirtyForNavigation(editForm: NgForm): boolean {
    return editForm.dirty;
  }

  /** Coordinates non-submit async actions owned by complex edit-page helpers. */
  setExternalPending(pending: boolean): void {
    this.pendingState.set(pending);
    this.bumpSubmitState();
  }

  protected saveDirect(saved: T): void {
    this.applySave(saved, this.registeredForm);
  }

  protected initialiseModel(model: T): T {
    return {
      ...model,
      objectState: model.objectState || 'ACTIVE',
      validationWarnings: model.validationWarnings || []
    };
  }

  protected prepareForSubmit(_model: T): void {
  }

  /** Runs on the cloned request body only; use for conversions the live form must not see. */
  protected prepareSubmitPayload(_payload: T): void {
  }

  private bufferDisplayFlag(state: WritableSignal<boolean>, next: boolean): boolean {
    const display = state();
    if (display !== next) {
      deferSignalStateWrite(() => {
        if (!this.destroyRef.destroyed) {
          state.set(next);
        }
      });
    }
    return display;
  }

  protected persist(payload: T): Observable<T> {
    return this.api.save(payload);
  }

  protected afterSave(_model: T): void {
  }

  /** Runs after the editable clone has replaced the previous form model. */
  protected afterEditingModelReset(_model: T): void {
  }

  private applySave(saved: T, editForm: NgForm | null): void {
    // Saved resources are no longer new: isNew-only defaults in initialiseModel /
    // afterSave must not re-apply to the server response.
    this.state.isNew.set(false);
    const updated = this.initialiseModel(saved);
    // Clear the dirty guard before afterSave: hooks may navigate immediately
    // (for example creative "to line item after save"). Stay-on-page saves are
    // registered again after the saved model has been installed.
    suspendSignalAdminResourceFormDirtyGuard(editForm, this.formState);
    this.afterSave(updated);
    this.lastStateModel = updated;
    this.editingModel = _.cloneDeep(updated);
    this.state.update(updated);
    this.afterEditingModelReset(this.editingModel);
    this.submitState.validationError = false;
    this.submitState.validSubmission = true;
    this.submitState.hasValidationWarnings = this.hasBlockingWarnings(updated);
    if (editForm) {
      markSignalAdminResourceFormPristine(editForm);
      this.registerFormState(editForm);
    }
    this.scheduleWarningsUpdate();
    this.bumpSubmitState();

    if (!UpdateSession.obtainData().authToken) {
      this.userProfileSaver.saveObj(updated, 'adnLastEdited');
      this.userProfileSaver.saveObj(updated, 'adnLastViewed');
    }
  }

  private applySaveError(response: any, editForm: NgForm): void {
    if ((response?.status === 400 || response?.status === 403 || response?.status === 422)
      && (response?.data?.type === 'ValidationResult' || response?.data?.type === 'ErrorMessage')) {
      handleApiValidationErrors(
        this.submitState,
        {...this.editingModel, resourceName: this.resourceName},
        response.data.errors || response.data,
        this.formControlErrors,
        editForm
      );
    } else {
      this.submitState.validationError = true;
    }
    this.submitState.validSubmission = false;
    this.bumpSubmitState();
  }

  private resetEditingModel(model: T): void {
    this.editingModel = _.cloneDeep(this.initialiseModel(model));
    this.afterEditingModelReset(this.editingModel);
    this.submitState.hasValidationWarnings = this.hasBlockingWarnings(this.editingModel);
    this.submitState.validationError = false;
    this.submitState.apiErrors = [];
    this.submitState.clientErrors = [];
    this.submitState.validSubmission = false;
    if (this.registeredForm) {
      markSignalAdminResourceFormPristine(this.registeredForm);
      this.scheduleWarningsUpdate();
    }
    this.bumpSubmitState();
  }

  private registerFormState(editForm: NgForm): void {
    this.formState.setCurrentForm({
      markDirty: () => editForm.control.markAsDirty(),
      isDirty: () => this.isFormDirtyForNavigation(editForm),
      resetSubmission: () => {
        this.submitState.validSubmission = false;
        this.bumpSubmitState();
      }
    });
  }

  private scheduleWarningsUpdate(): void {
    if (!this.registeredForm) {
      return;
    }
    if (this.warningsTimer) {
      clearTimeout(this.warningsTimer);
    }
    this.warningsTimer = setTimeout(() => {
      this.warningsTimer = null;
      updateFormWarnings(
        this.submitState,
        {...this.editingModel, resourceName: this.resourceName},
        this.localNetworkProfile.isPureMarketplacePlatform()
      );
      this.submitState.hasValidationWarnings = this.hasBlockingWarnings(this.editingModel);
      this.bumpSubmitState();
    }, 100);
  }

  private hasBlockingWarnings(model: T): boolean {
    return (model.validationWarnings || []).some((warning) => !!warning.preventsAdServing);
  }

  private bumpSubmitState(): void {
    this.submitVersion.update((version) => version + 1);
  }
}
